import type { IssueListItem } from '../../services/issueService';
import type { WorkflowStatusInfo } from '../../services/projectService';
import type { WorkflowTransitionsData, TransitionConditionType } from '../../services/workflowService';
export { ISSUE_TYPE_CONFIG } from '../../constants/issueTypeConfig';

// --- Board Settings ---

export interface BoardSettings {
  cardDisplayFields: {
    type: boolean;
    assignee: boolean;
    priority: boolean;
    dueDate: boolean;
    labels: boolean;
  };
}

export const DEFAULT_BOARD_SETTINGS: BoardSettings = {
  cardDisplayFields: {
    type: true,
    assignee: true,
    priority: true,
    dueDate: true,
    labels: false,
  },
};

// --- Column Definitions (CHG-041: dynamic) ---

export interface BoardColumnDef {
  id: string;
  title: string;
  color: string;
  filterFn: (issue: IssueListItem) => boolean;
  dropStatusId: string;
}

/** 워크플로우 상태 목록으로 동적 보드 컬럼 생성 */
export function buildDynamicColumns(statuses: WorkflowStatusInfo[]): BoardColumnDef[] {
  return statuses.map((ws) => ({
    id: ws.statusId,
    title: ws.name,
    color: ws.color,
    filterFn: (issue: IssueListItem) => issue.statusId === ws.statusId,
    dropStatusId: ws.statusId,
  }));
}

// --- CHG-042: Multi-workflow category columns ---

export type StatusCategory = 'TODO' | 'IN_PROGRESS' | 'DONE';

export interface CategoryColumnDef {
  category: StatusCategory;
  title: string;
  color: string;
  filterFn: (issue: IssueListItem) => boolean;
}

const CATEGORY_META: Record<StatusCategory, { title: string; color: string }> = {
  TODO: { title: '할 일', color: '#6b7280' },
  IN_PROGRESS: { title: '진행 중', color: '#3b82f6' },
  DONE: { title: '완료', color: '#22c55e' },
};

/** 다중 워크플로우 시 StatusCategory 기반 컬럼 생성 */
export function buildCategoryColumns(): CategoryColumnDef[] {
  return (['TODO', 'IN_PROGRESS', 'DONE'] as StatusCategory[]).map((cat) => ({
    category: cat,
    title: CATEGORY_META[cat].title,
    color: CATEGORY_META[cat].color,
    filterFn: (issue: IssueListItem) => issue.workflowStatus.category === cat,
  }));
}

/**
 * 이슈의 유효 워크플로우에서 타겟 카테고리의 첫 번째 상태 ID를 반환.
 * 이슈 유형별 워크플로우 매핑 데이터를 기반으로 결정한다.
 */
export function resolveTargetStatusId(
  issue: IssueListItem,
  targetCategory: StatusCategory,
  workflowGroups: { workflowId: string; statuses: WorkflowStatusInfo[] }[],
  issueTypeWorkflows: { issueType: string; workflow: { workflowId: string } }[],
  defaultWorkflowId: string,
): string | null {
  // 이슈 타입에 맞는 워크플로우 ID 결정
  const override = issueTypeWorkflows.find((m) => m.issueType === issue.issueType);
  const effectiveWorkflowId = override?.workflow.workflowId ?? defaultWorkflowId;

  // 해당 워크플로우의 상태 목록에서 타겟 카테고리의 첫 번째 상태 찾기
  const group = workflowGroups.find((g) => g.workflowId === effectiveWorkflowId);
  if (!group) return null;

  const targetStatus = group.statuses
    .filter((s) => s.category === targetCategory)
    .sort((a, b) => a.sortOrder - b.sortOrder)[0];

  return targetStatus?.statusId ?? null;
}

// --- Filter State ---

export interface BoardFilterState {
  types: string[];
  assignees: string[];
  priorities: string[];
  search: string;
}

export const EMPTY_BOARD_FILTERS: BoardFilterState = {
  types: [],
  assignees: [],
  priorities: [],
  search: '',
};

// --- Priority display ---

export const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  BLOCKER: { color: 'text-red-700', label: 'BLOCKER' },
  CRITICAL: { color: 'text-red-500', label: 'CRITICAL' },
  HIGH: { color: 'text-orange-500', label: 'HIGH' },
  MEDIUM: { color: 'text-yellow-500', label: 'MEDIUM' },
  LOW: { color: 'text-green-500', label: 'LOW' },
};


// --- Filter helpers ---

// --- CHG-043: Client-side transition rule evaluation for kanban ---

export type ProjectRole = 'PROJECT_MANAGER' | 'PROJECT_LEADER' | 'MEMBER' | 'STAKEHOLDER';

/** Evaluate REQUIRED_ROLES conditions on the client side (mirrors server evaluateConditions) */
function evaluateConditionsClient(
  conditions: { conditionType: TransitionConditionType; conditionValue: Record<string, unknown> }[],
  userProjectRole: ProjectRole | null,
  isSystemAdmin: boolean,
): { isAllowed: boolean; disabledReason: string | null } {
  if (isSystemAdmin) return { isAllowed: true, disabledReason: null };
  if (conditions.length === 0) return { isAllowed: true, disabledReason: null };

  for (const c of conditions) {
    if (c.conditionType === 'REQUIRED_ROLES') {
      const roles = (c.conditionValue as { roles?: string[] }).roles ?? [];
      if (roles.length > 0 && (!userProjectRole || !roles.includes(userProjectRole))) {
        return { isAllowed: false, disabledReason: '이 전환에 대한 권한이 없습니다' };
      }
    }
  }

  return { isAllowed: true, disabledReason: null };
}

/** Compute allowed target statuses for a given issue based on cached transition rules */
export function getClientAllowedTargets(
  currentStatusId: string,
  transitionData: WorkflowTransitionsData,
  userProjectRole: ProjectRole | null,
  isSystemAdmin: boolean,
): Map<string, { isAllowed: boolean; disabledReason: string | null; transitionName: string }> {
  const result = new Map<string, { isAllowed: boolean; disabledReason: string | null; transitionName: string }>();

  if (transitionData.transitionMode === 'FREE') {
    // FREE mode: all statuses except current are allowed
    for (const s of transitionData.statuses) {
      if (s.statusId !== currentStatusId) {
        result.set(s.statusId, { isAllowed: true, disabledReason: null, transitionName: s.name });
      }
    }
    return result;
  }

  // RESTRICTED mode: only matching transitions
  const matchingTransitions = transitionData.transitions.filter(
    (t) => t.fromStatusId === currentStatusId || t.fromStatusId === null,
  );

  for (const t of matchingTransitions) {
    // Skip if targeting current status
    if (t.toStatusId === currentStatusId) continue;

    const { isAllowed, disabledReason } = evaluateConditionsClient(
      t.conditions.map((c) => ({
        conditionType: c.conditionType,
        conditionValue: c.conditionValue as Record<string, unknown>,
      })),
      userProjectRole,
      isSystemAdmin,
    );

    const existing = result.get(t.toStatusId);
    // If there's already a better (allowed) transition for this target, keep it
    if (!existing || (!existing.isAllowed && isAllowed)) {
      result.set(t.toStatusId, { isAllowed, disabledReason, transitionName: t.name });
    }
  }

  return result;
}

/** Check if target category has any allowed transition (for multi-workflow boards) */
export function getClientAllowedCategoryTargets(
  currentStatusId: string,
  targetCategory: StatusCategory,
  transitionData: WorkflowTransitionsData,
  userProjectRole: ProjectRole | null,
  isSystemAdmin: boolean,
): { isAllowed: boolean; disabledReason: string | null; targetStatusId: string | null } {
  const allowedMap = getClientAllowedTargets(currentStatusId, transitionData, userProjectRole, isSystemAdmin);

  // Find statuses in the target category
  const categoryStatuses = transitionData.statuses.filter((s) => s.category === targetCategory);

  for (const s of categoryStatuses) {
    const target = allowedMap.get(s.statusId);
    if (target?.isAllowed) {
      return { isAllowed: true, disabledReason: null, targetStatusId: s.statusId };
    }
  }

  // Check if any transition exists but is not allowed
  for (const s of categoryStatuses) {
    const target = allowedMap.get(s.statusId);
    if (target) {
      return { isAllowed: false, disabledReason: target.disabledReason, targetStatusId: null };
    }
  }

  return { isAllowed: false, disabledReason: '현재 상태에서 해당 카테고리로 전환할 수 없습니다', targetStatusId: null };
}

export function applyBoardFilters(
  issues: IssueListItem[],
  filters: BoardFilterState,
): IssueListItem[] {
  return issues.filter((issue) => {
    if (filters.types.length > 0 && !filters.types.includes(issue.issueType)) return false;
    if (filters.assignees.length > 0) {
      const assigneeId = issue.assignee?.id ?? '';
      if (!filters.assignees.includes(assigneeId)) return false;
    }
    if (filters.priorities.length > 0 && !filters.priorities.includes(issue.priority)) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (
        !issue.issueId.toLowerCase().includes(q) &&
        !issue.title.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });
}
